import { createActionGroup, emptyProps, props } from '@ngrx/store';

import {
  AIChatMessage,
  ChatThread,
  FileAttachment,
  AiAction,
  ReasoningStep,
  DataOperationEvent,
  WorkflowPlan,
  AiActionExecutionState,
  InteractionOption,
  AiToolCall,
  AsyncProgress,
} from '../ai-chat.config';

export const AiChatActions = createActionGroup({
  source: 'AI Chat',
  events: {
    // Initialization
    'Init Chat': emptyProps(),
    'Init Chat Success': props<{ channelId: string; threads: ChatThread[] }>(),
    'Init Chat Failure': props<{ error: string }>(),
    'Clear Chat State': emptyProps(),

    // Thread Pagination
    'Load More Threads': emptyProps(),
    'Load More Threads Success': props<{ threads: ChatThread[] }>(),
    'Load More Threads Failure': props<{ error: string }>(),

    // Thread Selection & Creation
    'Select Thread': props<{ threadId: string }>(),
    'Select Thread Success': props<{ messages: AIChatMessage[] }>(),
    'Create New Thread': emptyProps(),
    'Create New Thread Success': props<{ thread: ChatThread }>(),
    'Open New Conversation': emptyProps(),

    // Explicit Thread Metadata Fetching
    'Fetch Thread': props<{ threadId: string }>(),
    'Fetch Thread Success': props<{ thread: ChatThread }>(),
    'Fetch Thread Failure': props<{ error: string }>(),

    // UI Configuration Metadata Fetching
    'Load Project Model': props<{ entityName: string }>(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'Load Project Model Success': props<{
      entityName: string;
      projectModel: any;
    }>(),
    'Load Project Model Failure': props<{
      entityName: string;
      error: string;
    }>(),

    // AI Functions Fetching
    'Load Available Ai Functions': emptyProps(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'Load Available Ai Functions Success': props<{ functions: any[] }>(),
    'Load Available Ai Functions Failure': props<{ error: string }>(),

    // User triggered actions
    'Send Message': props<{
      text: string;
      attachments?: FileAttachment[];
      messageType?: string;
      isConfirmation?: boolean;
      isShownInChat?: boolean;
      isHidden?: boolean;
      data?: Record<string, any>;
      aiFunctionIdOverride?: string;
    }>(),
    'Send Voice Message': props<{ file: File }>(),
    'Execute Ai Chat Trigger': props<{ prompt: string }>(),
    'Send Interaction Response': props<{
      messageId: string;
      allOptions: InteractionOption[];
      selectedOptions: InteractionOption[];
    }>(),
    'Skip Interaction': props<{ messageId: string }>(),

    // Internal State mutations
    'Add Message': props<{ message: AIChatMessage }>(),
    'Update Message Id': props<{ tempId: string; realId: string }>(),
    'Update Transcription': props<{ messageId: string; text: string }>(),
    'Set Ai Thinking': props<{ isThinking: boolean }>(),
    'Set Active Thread': props<{ threadId: string }>(),
    'Select Ai Function': props<{ aiFunctionId: string }>(),

    // API Call Results
    'Send Message Success': props<{
      aiMessage: AIChatMessage;
      tempAiMsgId?: string;
    }>(),
    'Send Message Failure': props<{ error: string }>(),
    'Dismiss Actions': props<{ messageId: string }>(),

    'Update Reasoning Steps': props<{
      messageId: string;
      step: ReasoningStep;
    }>(),
    'Update Tool Calls': props<{
      messageId: string;
      toolCalls: AiToolCall[];
    }>(),
    'Update Async Progress': props<{
      messageId: string;
      progress: AsyncProgress;
    }>(),

    // --- REVISED ACTION EXECUTION EVENTS ---
    'Execute Endpoint Actions': props<{
      actions: AiAction[];
      messageId: string;
      originatingRequestMessageId?: string;
    }>(),
    'Set Action Execution State': props<{
      messageId: string;
      actionDescription: string;
      actionIndex?: number;
      executionState: AiActionExecutionState;
    }>(),

    // --- WORKFLOW PLAN EXECUTION ---
    'Execute Workflow Plan': props<{ plan: WorkflowPlan; messageId: string }>(),
    'Set Workflow Execution State': props<{
      messageId: string;
      executionState: AiActionExecutionState;
    }>(),
    'Set Workflow Task Status': props<{
      messageId: string;
      taskIndex: number;
      status: 'pending' | 'in-progress' | 'completed' | 'failed';
    }>(),
    'Execute Jsonata': props<{ messageId: string; expression: string }>(),

    // --- WORKFLOW POLLING ---
    'Start Workflow Polling': props<{
      messageId: string;
      correlationId: string;
    }>(),
    'Stop Workflow Polling': props<{ messageId: string }>(),
    'Fetch Workflow Logs': props<{
      messageId: string;
      correlationId: string;
    }>(),

    // --- DECOUPLED DATA EVENTS ---
    'Log Data Operation': props<{ event: DataOperationEvent }>(),
  },
});
